import { useCallback, useMemo, useReducer } from 'react';

import {
	type DiffEntry,
	type SearchResult,
	addArrayElement,
	addObjectKey,
	ancestorPaths,
	deepClone,
	deepDiff,
	deleteAtPath,
	getVisibleEditTargets,
	inferType,
	renameKeyAtPath,
	searchJson,
	setAtPath,
	toggleNumericType,
} from './utils';

export interface EditorState {
	workingData: unknown;
	originalData: unknown;
	expandedPaths: Set<string>;
	editingPath: string | null;
	editingTarget: 'key' | 'value';
	sortAlpha: boolean;
	searchQuery: string;
	focusedPath: string | null;
}

type EditorAction =
	| { type: 'TOGGLE_EXPAND'; path: string }
	| { type: 'EXPAND_PATHS'; paths: string[] }
	| { type: 'SET_EDITING'; path: string | null; target: 'key' | 'value' }
	| { type: 'COMMIT_EDIT'; path: string; raw: string }
	| { type: 'RENAME_KEY'; path: string; newKey: string }
	| {
			type: 'COMMIT_AND_TAB';
			path: string;
			target: 'key' | 'value';
			raw: string;
			direction: 'forward' | 'backward';
	  }
	| { type: 'DELETE_PATH'; path: string }
	| { type: 'TOGGLE_NUMERIC_TYPE'; path: string; currentValue: unknown }
	| { type: 'SET_SORT_ALPHA'; value: boolean }
	| { type: 'SET_SEARCH_QUERY'; query: string }
	| { type: 'SET_FOCUSED_PATH'; path: string | null }
	| { type: 'RESET'; data: unknown }
	| { type: 'ADD_OBJECT_KEY'; path: string; key: string }
	| { type: 'ADD_ARRAY_ELEMENT'; path: string };

function applyEdit(
	state: EditorState,
	path: string,
	target: 'key' | 'value',
	raw: string,
): { workingData: unknown; resolvedPath: string } {
	if (target === 'key') {
		const result = renameKeyAtPath(state.workingData, path, raw);
		return { workingData: result.data, resolvedPath: result.newPath };
	}
	const inferred = inferType(raw);
	return {
		workingData: setAtPath(state.workingData, path, inferred),
		resolvedPath: path,
	};
}

function reducer(state: EditorState, action: EditorAction): EditorState {
	switch (action.type) {
		case 'TOGGLE_EXPAND': {
			const next = new Set(state.expandedPaths);
			if (next.has(action.path)) {
				next.delete(action.path);
			} else {
				next.add(action.path);
			}
			return { ...state, expandedPaths: next };
		}
		case 'EXPAND_PATHS': {
			const next = new Set(state.expandedPaths);
			for (const p of action.paths) next.add(p);
			return { ...state, expandedPaths: next };
		}
		case 'SET_EDITING':
			return {
				...state,
				editingPath: action.path,
				editingTarget: action.target,
			};
		case 'COMMIT_EDIT': {
			const inferred = inferType(action.raw);
			const newData = setAtPath(state.workingData, action.path, inferred);
			return { ...state, workingData: newData, editingPath: null };
		}
		case 'RENAME_KEY': {
			const result = renameKeyAtPath(
				state.workingData,
				action.path,
				action.newKey,
			);
			return { ...state, workingData: result.data, editingPath: null };
		}
		case 'COMMIT_AND_TAB': {
			const { workingData, resolvedPath } = applyEdit(
				state,
				action.path,
				action.target,
				action.raw,
			);
			const targets = getVisibleEditTargets(
				workingData,
				state.expandedPaths,
				state.sortAlpha,
			);
			const idx = targets.findIndex(
				(t) => t.path === resolvedPath && t.target === action.target,
			);
			const nextIdx = action.direction === 'forward' ? idx + 1 : idx - 1;

			if (nextIdx >= 0 && nextIdx < targets.length) {
				const next = targets[nextIdx];
				return {
					...state,
					workingData,
					editingPath: next.path,
					editingTarget: next.target,
				};
			}
			return { ...state, workingData, editingPath: null };
		}
		case 'DELETE_PATH': {
			const newData = deleteAtPath(state.workingData, action.path);
			return { ...state, workingData: newData, editingPath: null };
		}
		case 'TOGGLE_NUMERIC_TYPE': {
			const toggled = toggleNumericType(action.currentValue);
			const newData = setAtPath(state.workingData, action.path, toggled);
			return { ...state, workingData: newData };
		}
		case 'SET_SORT_ALPHA':
			return { ...state, sortAlpha: action.value };
		case 'SET_SEARCH_QUERY':
			return { ...state, searchQuery: action.query };
		case 'SET_FOCUSED_PATH':
			return { ...state, focusedPath: action.path };
		case 'ADD_OBJECT_KEY': {
			const newData = addObjectKey(state.workingData, action.path, action.key);
			const next = new Set(state.expandedPaths);
			next.add(action.path);
			return { ...state, workingData: newData, expandedPaths: next };
		}
		case 'ADD_ARRAY_ELEMENT': {
			const newData = addArrayElement(state.workingData, action.path);
			const next = new Set(state.expandedPaths);
			next.add(action.path);
			return { ...state, workingData: newData, expandedPaths: next };
		}
		case 'RESET':
			return createInitialState(action.data);
		default:
			return state;
	}
}

function createInitialState(data: unknown): EditorState {
	return {
		workingData: deepClone(data),
		originalData: deepClone(data),
		expandedPaths: new Set<string>(['']),
		editingPath: null,
		editingTarget: 'value',
		sortAlpha: false,
		searchQuery: '',
		focusedPath: null,
	};
}

export function useJsonEditor(data: unknown) {
	const [state, dispatch] = useReducer(reducer, data, createInitialState);

	const toggleExpand = useCallback(
		(path: string) => dispatch({ type: 'TOGGLE_EXPAND', path }),
		[],
	);

	const expandPaths = useCallback(
		(paths: string[]) => dispatch({ type: 'EXPAND_PATHS', paths }),
		[],
	);

	const setEditing = useCallback(
		(path: string | null, target: 'key' | 'value' = 'value') =>
			dispatch({ type: 'SET_EDITING', path, target }),
		[],
	);

	const commitEdit = useCallback(
		(path: string, raw: string) => dispatch({ type: 'COMMIT_EDIT', path, raw }),
		[],
	);

	const renameKey = useCallback(
		(path: string, newKey: string) =>
			dispatch({ type: 'RENAME_KEY', path, newKey }),
		[],
	);

	const commitAndTab = useCallback(
		(
			path: string,
			target: 'key' | 'value',
			raw: string,
			direction: 'forward' | 'backward',
		) => dispatch({ type: 'COMMIT_AND_TAB', path, target, raw, direction }),
		[],
	);

	const deletePath = useCallback(
		(path: string) => dispatch({ type: 'DELETE_PATH', path }),
		[],
	);

	const toggleNumeric = useCallback(
		(path: string, currentValue: unknown) =>
			dispatch({ type: 'TOGGLE_NUMERIC_TYPE', path, currentValue }),
		[],
	);

	const addKey = useCallback(
		(path: string, key: string) =>
			dispatch({ type: 'ADD_OBJECT_KEY', path, key }),
		[],
	);

	const addElement = useCallback(
		(path: string) => dispatch({ type: 'ADD_ARRAY_ELEMENT', path }),
		[],
	);

	const setSortAlpha = useCallback(
		(value: boolean) => dispatch({ type: 'SET_SORT_ALPHA', value }),
		[],
	);

	const setSearchQuery = useCallback(
		(query: string) => dispatch({ type: 'SET_SEARCH_QUERY', query }),
		[],
	);

	const navigateToResult = useCallback((path: string) => {
		const ancestors = ancestorPaths(path);
		dispatch({ type: 'EXPAND_PATHS', paths: ancestors });
		dispatch({ type: 'SET_FOCUSED_PATH', path });
	}, []);

	const clearFocus = useCallback(
		() => dispatch({ type: 'SET_FOCUSED_PATH', path: null }),
		[],
	);

	const reset = useCallback(
		(newData: unknown) => dispatch({ type: 'RESET', data: newData }),
		[],
	);

	const searchResults: SearchResult[] = useMemo(
		() => searchJson(state.workingData, state.searchQuery),
		[state.workingData, state.searchQuery],
	);

	const diffs: DiffEntry[] = useMemo(
		() => deepDiff(state.originalData, state.workingData),
		[state.originalData, state.workingData],
	);

	return {
		state,
		searchResults,
		diffs,
		toggleExpand,
		expandPaths,
		setEditing,
		commitEdit,
		renameKey,
		commitAndTab,
		deletePath,
		toggleNumeric,
		addKey,
		addElement,
		setSortAlpha,
		setSearchQuery,
		navigateToResult,
		clearFocus,
		reset,
	};
}
